<script lang="ts" setup>
import ProjectsDropDown, {
  type ProjectsDropDownSelectedProject,
} from '@/components/common/projects/ProjectsDropDown.vue';
import {
  PhBookOpen,
  PhChat,
  PhCheck,
  PhPencilSimple,
  PhUser,
  PhX,
} from '@phosphor-icons/vue';
import { PermissionType, ReviewPermissionType } from 'cesg-shared/dist/models';
import { useI18n } from 'vue-i18n';

export interface PermissionModalViewData {
  user: {
    id: string;
    name: string;
  };
  excludeProjectIds?: string[];
}

export interface PermissionModalPermissions {
  overview: PermissionType;
  budget: PermissionType;
  forecast: PermissionType;
  ata: PermissionType;
  invoice: PermissionType;
  files: PermissionType;
  review: ReviewPermissionType;
}

export interface PermissionModalViewProps {
  data: PermissionModalViewData;
  disable?: {
    save?: boolean;
  };
}

export type PermissionModalAction =
  | { type: 'close' }
  | { type: 'cancel' }
  | {
      type: 'save';
      permissions: PermissionModalPermissions;
      projects: ProjectsDropDownSelectedProject[];
    };

export type PermissionModalEmits = {
  (event: 'action', payload: PermissionModalAction): void;
};

const { t } = useI18n();

const props = defineProps<PermissionModalViewProps>();
const permissionsModel = defineModel<PermissionModalPermissions>(
  'permissions',
  {
    required: true,
  },
);
const selectedProjectsModel = defineModel<ProjectsDropDownSelectedProject[]>(
  'selectedProjects',
  { required: true },
);
const emit = defineEmits<PermissionModalEmits>();

type StandardResourceKey = Exclude<keyof PermissionModalPermissions, 'review'>;

const standardResources: {
  key: StandardResourceKey;
  labelKey: string;
  hintKey?: string;
}[] = [
  { key: 'overview', labelKey: 'permissionModalView.resources.overview' },
  { key: 'budget', labelKey: 'permissionModalView.resources.budget' },
  { key: 'forecast', labelKey: 'permissionModalView.resources.forecast' },
  { key: 'ata', labelKey: 'permissionModalView.resources.ata' },
  { key: 'invoice', labelKey: 'permissionModalView.resources.invoice' },
  {
    key: 'files',
    labelKey: 'permissionModalView.resources.files',
    // SharePoint has no permission level meaning "can comment" (D13), so COMMENT is bound to the
    // built-in Read role — the same effective file access as VIEW. Surfaced as a hint so the tier
    // behaving unlike the other five resources is documented where it is chosen.
    hintKey: 'permissionModalView.resources.filesCommentHint',
  },
];

const standardPermissionTypes: {
  value: PermissionType;
  labelKey: string;
  icon: typeof PhX;
}[] = [
  {
    value: PermissionType.DISABLED,
    labelKey: 'permissionModalView.permissionTypes.disabled',
    icon: PhX,
  },
  {
    value: PermissionType.VIEW,
    labelKey: 'permissionModalView.permissionTypes.view',
    icon: PhBookOpen,
  },
  {
    value: PermissionType.COMMENT,
    labelKey: 'permissionModalView.permissionTypes.comment',
    icon: PhChat,
  },
  {
    value: PermissionType.EDIT,
    labelKey: 'permissionModalView.permissionTypes.edit',
    icon: PhPencilSimple,
  },
];

const reviewPermissionTypes: {
  value: ReviewPermissionType;
  labelKey: string;
  icon: typeof PhX;
}[] = [
  {
    value: ReviewPermissionType.PROHIBITED,
    labelKey: 'permissionModalView.prohibited',
    icon: PhX,
  },
  {
    value: ReviewPermissionType.ALLOWED,
    labelKey: 'permissionModalView.allowed',
    icon: PhCheck,
  },
];
</script>

<template>
  <div class="modal is-active" role="dialog" aria-modal="true">
    <div
      class="modal-background"
      @click="emit('action', { type: 'close' })"
    ></div>

    <div class="modal-card permission-modal-card">
      <header class="modal-card-head">
        <p class="modal-card-title">{{ t('permissionModalView.title') }}</p>
        <button
          class="delete"
          aria-label="close"
          type="button"
          @click="emit('action', { type: 'close' })"
        ></button>
      </header>

      <section class="modal-card-body">
        <p class="permission-subtitle">
          <PhUser :size="20" weight="bold" class="user-icon" />
          <strong>{{ props.data.user.name }}</strong>
        </p>

        <div class="table-wrap">
          <table class="permission-table">
            <thead>
              <tr>
                <th class="resource-col"></th>
                <th
                  v-for="pt in standardPermissionTypes"
                  :key="pt.value"
                  class="type-col"
                >
                  {{ t(pt.labelKey) }}
                  <component
                    :is="pt.icon"
                    :size="16"
                    weight="bold"
                    class="type-icon"
                  />
                </th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="resource in standardResources" :key="resource.key">
                <td class="resource-label">
                  {{ t(resource.labelKey) }}
                  <span
                    v-if="resource.hintKey"
                    class="resource-hint"
                    :title="t(resource.hintKey)"
                    >?</span
                  >
                </td>
                <td
                  v-for="pt in standardPermissionTypes"
                  :key="pt.value"
                  class="radio-cell"
                >
                  <label
                    class="perm-btn"
                    :class="{
                      'is-active': permissionsModel[resource.key] === pt.value,
                    }"
                  >
                    <input
                      type="radio"
                      :name="`perm-${resource.key}`"
                      :value="pt.value"
                      v-model="permissionsModel[resource.key]"
                    />
                  </label>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <div class="table-wrap">
          <table class="permission-table review-table">
            <thead>
              <tr>
                <th class="resource-col"></th>
                <th
                  v-for="rt in reviewPermissionTypes"
                  :key="rt.value"
                  class="type-col"
                >
                  {{ t(rt.labelKey) }}
                  <component
                    :is="rt.icon"
                    :size="16"
                    weight="bold"
                    class="type-icon"
                  />
                </th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td class="resource-label">
                  {{ t('permissionModalView.review') }}
                </td>
                <td
                  v-for="rt in reviewPermissionTypes"
                  :key="rt.value"
                  class="radio-cell"
                >
                  <label
                    class="perm-btn"
                    :class="{
                      'is-active': permissionsModel.review === rt.value,
                    }"
                  >
                    <input
                      type="radio"
                      name="perm-review"
                      :value="rt.value"
                      v-model="permissionsModel.review"
                    />
                  </label>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <div class="project-picker">
          <ProjectsDropDown
            :data="{
              placeholder: t('permissionModalView.projectDropDown'),
              excludeProjectIds: [
                ...(props.data.excludeProjectIds ?? []),
                ...selectedProjectsModel.map((p) => p.id),
              ],
              includeArchived: true,
              teleport: true,
            }"
            @action="
              selectedProjectsModel = [...selectedProjectsModel, $event.project]
            "
          />
          <div
            class="selected-project-pills"
            v-if="selectedProjectsModel.length > 0"
          >
            <span
              v-for="project in selectedProjectsModel"
              :key="project.id"
              class="selected-project-pill"
              :class="{
                'is-closed': ['Avslutad', 'Avslutat'].includes(
                  project.status ?? '',
                ),
              }"
            >
              {{ project.number }} - {{ project.name }}
              <button
                type="button"
                class="remove-selected-project"
                @click="
                  selectedProjectsModel = selectedProjectsModel.filter(
                    (p) => p.id !== project.id,
                  )
                "
                :aria-label="`Remove ${project.name}`"
              >
                <PhX :size="11" weight="bold" />
              </button>
            </span>
          </div>
        </div>
      </section>

      <footer class="modal-card-foot actions">
        <button
          type="button"
          class="button is-light"
          @click="emit('action', { type: 'cancel' })"
        >
          {{ t('cancel') }}
        </button>
        <button
          type="button"
          class="button is-primary"
          @click="
            emit('action', {
              type: 'save',
              permissions: permissionsModel,
              projects: selectedProjectsModel,
            })
          "
          :disabled="props.disable?.save || selectedProjectsModel.length === 0"
        >
          {{ t('save') }}
        </button>
      </footer>
    </div>
  </div>
</template>

<style scoped lang="scss">
.project-picker {
  margin-top: 1.25rem;
  display: flex;
  flex-direction: column;
  gap: 0.6rem;
}

.selected-project-pills {
  display: flex;
  flex-wrap: wrap;
  gap: 0.4rem;
}

.selected-project-pill {
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  border: 1px solid var(--primary, #485fc7);
  border-radius: 999px;
  padding: 0.35rem 0.7rem 0.35rem 1rem;
  background: rgba(72, 95, 199, 0.1);
  color: #1f2937;
  font-size: 0.9rem;
  font-weight: 600;

  &.is-closed {
    border-color: #cfd3dc;
    background: #f2f4f7;
    color: #374151;
  }
}

.remove-selected-project {
  border: 0;
  background: transparent;
  padding: 0;
  cursor: pointer;
  color: inherit;
  display: inline-flex;
  align-items: center;
  opacity: 0.6;

  &:hover {
    opacity: 1;
  }
}

.add-mode-label {
  color: #6b7280;
}

.permission-modal-card {
  width: min(640px, 94vw);
  max-height: calc(100dvh - 4rem);
  display: flex;
  flex-direction: column;

  .modal-card-head {
    box-shadow: none;
  }
}

.modal-card-body {
  padding: 1.5rem;
  overflow-y: auto;
  flex: 1;
  min-height: 0;
}

.permission-subtitle {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 1.25rem;
  font-size: 1.15rem;
  color: #4a4a4a;

  .user-icon {
    color: var(--primary, #485fc7);
    flex-shrink: 0;
  }

  .separator {
    color: #9ca3af;
  }
}

.table-wrap {
  border: 1px solid #e5e7eb;
  border-radius: 0.5rem;
  overflow: hidden;
  margin-bottom: 1.5rem;

  &:last-child {
    margin-bottom: 0;
  }
}

.permission-table {
  width: 100%;
  border-collapse: collapse;
  margin-bottom: 0;

  thead th {
    font-size: 0.8rem;
    font-weight: 600;
    color: #6b7280;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    padding: 0.4rem 0.5rem;
    text-align: center;

    &.type-col {
      vertical-align: middle;

      .type-icon {
        display: inline;
        vertical-align: middle;
        margin-right: 0.25rem;
      }
    }

    &.resource-col {
      text-align: left;
      min-width: 110px;
    }
  }

  tbody tr {
    border-top: 1px solid #f0f0f0;

    &:first-child {
      border-top: 2px solid #e5e7eb;
    }
  }

  td {
    padding: 0.55rem 0.5rem;
    vertical-align: middle;

    &.resource-label {
      font-size: 0.92rem;
      font-weight: 500;
      color: #374151;
      white-space: nowrap;
      transition: color 0.12s;

      .resource-hint {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        width: 1rem;
        height: 1rem;
        margin-left: 0.3rem;
        border-radius: 50%;
        background: #e5e7eb;
        color: #6b7280;
        font-size: 0.65rem;
        font-weight: 700;
        cursor: help;
      }
    }

    &.radio-cell {
      text-align: center;
    }
  }

  tbody tr:has(.perm-btn:hover) {
    background: rgba(72, 95, 199, 0.06);
  }

  tbody tr:has(.perm-btn:hover) .resource-label {
    color: var(--primary, #3b54c4);
    font-weight: 700;
  }
}

.perm-btn {
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 1.75rem;
  height: 1.75rem;
  border: 2px solid #d1d5db;
  border-radius: 50%;
  cursor: pointer;
  background: white;
  transition:
    border-color 0.12s,
    background 0.12s;

  input[type='radio'] {
    display: none;
  }

  &.is-active {
    border-color: var(--primary, #485fc7);
    background: var(--primary, #485fc7);

    &::after {
      content: '';
      position: absolute;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
      width: 0.55rem;
      height: 0.55rem;
      border-radius: 50%;
      background: white;
    }
  }

  &:hover:not(.is-active) {
    border-color: var(--primary, #485fc7);
    background: rgba(72, 95, 199, 0.08);
  }
}

.actions {
  justify-content: flex-end;
  gap: 1rem;
}
</style>
